import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { hitNotifyForStock } from 'aster-foundation/core/epharmacy/Product/redux/action';
import { REDIRECT_URI } from 'aster-foundation/core/common/utils/ApiConst';
import { IC_BELL_NOTIFY, IC_BELL_NOTIFY_WHITE, IC_NOTIFIED, IC_NOTIFIED_BELL, IC_NOTIFIED_ME, IC_NOTIFY_ME, IC_TICK_ORANGE, NOTIFY_ME_TOOLTIP } from '@common/Assets/images';
import { errorHandlingEpharmacy } from '@common/Utility/common-utils';
import { useAuth } from '@common/AuthService';
import { ButtonBase } from '@common/Component/Forms';
import { EventEmitter } from '@common/Utility/eventEmitter';
import { ONE_ASTER_CATEGORY } from '@common/Utility/constants';
import { dataLayerSendEvent } from '../EpharmacyAnalytics';
import NotifyMeStyled from './NotifyMeStyled';

const NotifyMe = ({ outOfStock, product, enableFullWidth, minWidth, size, userLogin, zoneData, analyticsData, newButton, isPdpQuickView = false }) => {
  const dispatch = useDispatch();
  const [isInNotifyList, setIsInNotifyList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const [notifiedHover, setNotifiedHover] = useState(false);
  const { authCtx } = useAuth();
  const router = useRouter();

  const getNotificationList = () => {
    if (typeof window === 'undefined') {
      return null;
    }
    if (sessionStorage.getItem('notificationList')) {
      return JSON.parse(sessionStorage.getItem('notificationList')) || [];
    }

    return [];
  };

  const isProductDetailsPage = () => window.location.href.indexOf('/p/') > 0;

  const isProductInNotificationList = () => getNotificationList()?.includes(product?.sku);

  const insertProductToNotifyList = () => {
    const notificationList = getNotificationList();
    if (!notificationList.includes(product?.sku)) {
      notificationList.push(product?.sku);
      sessionStorage.setItem('notificationList', JSON.stringify(notificationList));
      setIsInNotifyList(true);
      setNotifiedHover((prev) => {
        if (!prev) {
          setTimeout(() => {
            setNotifiedHover(false);
          }, 5000);
        }
        return !prev;
      });
    }
  };

  const handleHover = () => {
    setHover((prev) => {
      if (!prev) {
        setTimeout(() => {
          setHover(false);
        }, 5000);
      }
      return !prev;
    });
  };

  const handleNotifiedHover = () => {
    setNotifiedHover((prev) => {
      if (!prev) {
        setTimeout(() => {
          setNotifiedHover(false);
        }, 5000);
      }
      return !prev;
    });
  };

  const handleNotifyMe = () => {
    if (authCtx && !authCtx?.authenticated) {
      let redirectUri = REDIRECT_URI + router.asPath;
      const hasAutoNotify = router.asPath.includes('autoNotify=true');
      const hasSku = router.asPath.includes('sku=');

      if (hasAutoNotify) {
        redirectUri = redirectUri.replace(/([&?])autoNotify=true/, '');
      }
      if (hasSku) {
        redirectUri = redirectUri.replace(/([&?])sku=[^&]*/, '');
      }

      const queryCharacter = redirectUri.indexOf('?') > 0 ? '&' : '?';
      redirectUri += `${queryCharacter}autoNotify=true&sku=${product?.sku}`;

      authCtx.login({
        redirectUri: encodeURIComponent(redirectUri),
      });
      return;
    }

    if (isProductInNotificationList()) return;

    const userZone = localStorage.getItem('userZone');
    const params = {
      userId: userLogin?.id,
      email: userLogin?.telecom.find((u) => u.system === 'email')?.value,
      eventType: 'PRODUCT',
      eventId: product?.sku,
    };

    if (zoneData?.length > 0) {
      params.zones = zoneData;
    } else if (userZone) {
      params.zones = userZone?.split(',');
    }
    setIsLoading(true);
    dispatch(
      hitNotifyForStock({
        params,
        onSuccess: () => {
          insertProductToNotifyList();
          setIsLoading(false);
          if(isPdpQuickView) {
            toast.success("Noted, we'll inform you when it's available.")
          } else {
            EventEmitter.dispatch('setToastControlMessage', { message: "Noted, we'll inform you when it's available." });
          }
          if (isProductDetailsPage()) {
            EventEmitter.dispatch('productAddedToNotify', { sku: product?.sku });
          }
          dataLayerSendEvent('notify_me_button_click',{
            myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
            screen_name: analyticsData?.screenName,
            page_type: analyticsData?.pageType,
            product_name: analyticsData?.productName,
            product_sku: analyticsData?.productSku,
            referrer_page: analyticsData?.referrerPage
          });
        },
        onFailure: (err) => {
          setIsLoading(false);
          errorHandlingEpharmacy(err);
        },
      }),
    );
  };

  useEffect(() => {
    setIsInNotifyList(isProductInNotificationList());
    if (router?.query?.autoNotify && userLogin?.id && product?.sku === router?.query?.sku) {
      handleNotifyMe();
    }
  }, [userLogin]);

  useEffect(() => {
    if (isProductDetailsPage()) {
      EventEmitter.subscribe('productAddedToNotify', ({ sku }) => {
        if (product?.sku === sku) {
          setIsInNotifyList(true);
        }
      });
    }
  }, []);

  if (isInNotifyList) {
    return (
      <NotifyMeStyled>
        {!newButton ? <ButtonBase
          data-test="youWillBeNotifiedButton"
          enableFullWidth={enableFullWidth}
          disabled
          customClass="disabled notified"
          textClass="notified-text"
          minWidth={minWidth}
          icon={IC_TICK_ORANGE}
          iconPosition="left"
          iconWidth="20px"
          iconHeight="16px"
          size={size}
          variant="outline-danger"
          text="You'll be notified"
        /> :
        <div className='d-flex'>
        {notifiedHover && <Image src ={IC_NOTIFIED_ME.src} width={99} height={24} />}
        <div style={{width: '60px', height: '60px'}}>
          <Image 
            src={IC_NOTIFIED.src} 
            height={50} 
            width={50}
            onMouseEnter={handleNotifiedHover} 
            />
        </div>
      </div>
        }
      </NotifyMeStyled>
    );
  }

  if (outOfStock && !isInNotifyList) {
    return (
      <NotifyMeStyled>
        {!newButton ? <ButtonBase
          data-test="notifyMeButton"
          enableFullWidth={enableFullWidth}
          minWidth={minWidth}
          icon={IC_BELL_NOTIFY}
          iconPosition="left"
          iconWidth="19px"
          iconHeight="20px"
          iconHover={IC_BELL_NOTIFY_WHITE}
          size={size}
          variant="outline-danger"
          disabled={isLoading}
          customClass="notify-me"
          text={isLoading ? 'Loading...' : 'Notify Me'}
          onClick={() => {
            handleNotifyMe();
          }}
        /> : 
        <div className='d-flex'>
          {hover && <Image src ={NOTIFY_ME_TOOLTIP.src} width={72} height={24} />}
          <Image 
            src={IC_NOTIFY_ME.src} 
            height={60} 
            width={60} 
            onMouseEnter={handleHover}
            onClick={handleNotifyMe}
            />
        </div>
        
        }
      </NotifyMeStyled>
    );
  }

  return null;
};

NotifyMe.propTypes = {
  outOfStock: PropTypes.bool,
  product: PropTypes.object,
  size: PropTypes.string,
  enableFullWidth: PropTypes.bool,
  minWidth: PropTypes.string,
  userLogin: PropTypes.object.isRequired,
  zoneData: PropTypes.array.isRequired,
  analyticsData: PropTypes.object.isRequired,
  newButton: PropTypes.bool,
};

NotifyMe.defaultProps = {
  outOfStock: false,
  product: {},
  size: 'sm',
  enableFullWidth: false,
  minWidth: '140px',
  newButton: false,
};

export default NotifyMe;
